import { JsxElement } from "@/utils/jsxElementTypes";
import { SCREEN_SIZE_NAME_TO_SIZE } from "@/utils/constants";
import { Storyboard } from "@/utils/types";

export const CANVAS_WIDTH = 100000;
export const CANVAS_HEIGHT = 100000;

export const DRAG_IN_INITIAL_WIDTH = 800;
export const DRAG_IN_INITIAL_HEIGHT = 600;

// When creating a storyboard from text
// TODO: Scale by type of text element
export const DEFAULT_STORYBOARD_TEXT_WIDTH = 200;
export const DEFAULT_STORYBOARD_TEXT_HEIGHT = 80;

export const DRAG_IN_LARGER_WIDTH =
  SCREEN_SIZE_NAME_TO_SIZE["MacBook Pro 14”"].width;
export const DRAG_IN_LARGER_HEIGHT =
  SCREEN_SIZE_NAME_TO_SIZE["MacBook Pro 14”"].height;

export enum StoryboardChangeType {
  MOVE = "move",
  RESIZE_BOTTOM_RIGHT = "resize-bottom-right",
  RESIZE_BOTTOM_LEFT = "resize-bottom-left",
  RESIZE_TOP_RIGHT = "resize-top-right",
  RESIZE_TOP_LEFT = "resize-top-left",
  RESIZE_TOP = "resize-top",
  RESIZE_LEFT = "resize-left",
  RESIZE_BOTTOM = "resize-bottom",
  RESIZE_RIGHT = "resize-right",
}

/**
 * If storyboardId is not provided, this is the position from a canvas callback, and it simply shifts
 * to the canvas coordinates ((0, 0) is middle)
 *
 * If storyboardId is provided, it is assumed pointX and pointY are values from a callback inside the iframe
 *
 * It also returns the mouseX & Y which are values as if they were on the canvas
 */
export const getPointOnCanvasFromInsideIframe = (
  pointX: number,
  pointY: number,
  canvasRef: any,
  iframesRef: any,
  storyboardId?: string
) => {
  // @ts-ignore
  const canvasBoundingRect = canvasRef?.current?.getBoundingClientRect();

  if (!canvasBoundingRect) {
    return { x: pointX, y: pointY };
  }

  let mouseX = pointX;
  let mouseY = pointY;
  if (storyboardId) {
    // Convert mouseX, mouseY to the position in the entire canvas
    const iframeBoundingRect =
      iframesRef?.current?.[storyboardId]?.["ref"]?.getBoundingClientRect();
    const iframeWidth = iframesRef?.current?.[storyboardId]?.["width"];
    const iframeHeight = iframesRef?.current?.[storyboardId]?.["height"];

    if (!iframeBoundingRect || !iframeWidth || !iframeHeight) {
      return { x: pointX, y: pointY };
    }

    mouseX =
      (mouseX / iframeWidth) * iframeBoundingRect.width +
      iframeBoundingRect.left;
    mouseY =
      (mouseY / iframeHeight) * iframeBoundingRect.height +
      iframeBoundingRect.top;
  }

  const pointOnCanvasX =
    ((mouseX - canvasBoundingRect.left) / canvasBoundingRect.width) *
      CANVAS_WIDTH -
    CANVAS_WIDTH / 2;
  const pointOnCanvasY =
    ((mouseY - canvasBoundingRect.top) / canvasBoundingRect.height) *
      CANVAS_HEIGHT -
    CANVAS_HEIGHT / 2;

  return { x: pointOnCanvasX, y: pointOnCanvasY, mouseX, mouseY };
};

/**
 * Converts the coordinates from the storyboard to the coordinates within the iframe container.
 * @param {number} xInStoryboard - The x-coordinate in the storyboard.
 * @param {number} yInStoryboard - The y-coordinate in the storyboard.
 * @param {string} storyboardId - The ID of the storyboard.
 * @returns {{x: number, y: number}} - The converted coordinates within the iframe.
 */
export const normalizeToIFrameCoordinates = (
  iframesRef: any,
  xInStoryboard,
  yInStoryboard,
  storyboardId
) => {
  let mouseX = xInStoryboard;
  let mouseY = yInStoryboard;

  // Convert mouseX, mouseY to the position in the entire canvas
  const iframeBoundingRect =
    iframesRef?.current?.[storyboardId]?.["ref"]?.getBoundingClientRect();
  const iframeWidth = iframesRef?.current?.[storyboardId]?.["width"];
  const iframeHeight = iframesRef?.current?.[storyboardId]?.["height"];

  if (iframeBoundingRect) {
    mouseX =
      (xInStoryboard / iframeWidth) * iframeBoundingRect.width +
      iframeBoundingRect.left;
    mouseY =
      (yInStoryboard / iframeHeight) * iframeBoundingRect.height +
      iframeBoundingRect.top;
  }

  return { x: mouseX, y: mouseY };
};

export const getStoryboardForPoint = (
  canvas: any,
  pointOnCanvasX: number,
  pointOnCanvasY: number,
  excludeStoryboardId?: string
): Storyboard | null => {
  const candidateStoryboards = canvas?.storyboards
    ?.filter(
      (storyboard: any) => storyboard.deleted !== true && !storyboard.preCreated
    )
    .filter((storyboard: any) => {
      if (
        storyboard.x <= pointOnCanvasX &&
        storyboard.x + storyboard.width >= pointOnCanvasX &&
        (!excludeStoryboardId || excludeStoryboardId !== storyboard.id)
      ) {
        if (
          storyboard.y <= pointOnCanvasY &&
          storyboard.y + storyboard.height >= pointOnCanvasY
        ) {
          return true;
        }
      }
    });

  let storyboardWithGreatestZIndex = null;
  let greatestZIndex = -1;
  candidateStoryboards?.forEach((storyboard: any) => {
    if (storyboard.zIndex > greatestZIndex) {
      greatestZIndex = storyboard.zIndex;
      storyboardWithGreatestZIndex = storyboard;
    }
  });

  return storyboardWithGreatestZIndex;
};

export const getImageUrlForElement = (metadata: any, element: JsxElement) => {
  if (!element) {
    return null;
  }

  let imagePath = null;

  const fileHash = element.fileHash;

  if (fileHash && metadata[fileHash]) {
    if (metadata[fileHash].error) {
      // @ts-expect-error TS2322
      imagePath = "/images/cross-button.png";
    } else {
      imagePath = metadata[fileHash].previewUrl;
    }
  } else if (
    element?.type === "storybook-component" ||
    element?.type === "component"
  ) {
    // @ts-expect-error TS2322
    imagePath = "/images/placeholder.png";
  }

  if (element?.type === "storybook-component") {
    let storyName = element.filename.split("/").pop();
    let componentName = element.componentName;
    let library = element.library;

    if (library && storyName && componentName) {
      // @ts-expect-error TS2322
      imagePath = `https://storage.googleapis.com/tempo-image-previews/${library}/${storyName}/${componentName}.png`;
    }
  } else if (
    // @ts-ignore
    element?.type === "reactcomponent"
  ) {
    // @ts-ignore
    imagePath = element.image_url;
  }

  return `${
    import.meta.env.VITE_BACKEND_BASE_URL
  }/proxy-asset?url=${imagePath}`;
};
